import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { getInvoices, generateMonthlyInvoices, markInvoicePaid, generateInvoiceForClub, updateInvoiceStatus, deleteInvoice, type Invoice } from "../../services/invoices";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import DateInput from "../../components/DateInput";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { formatDate } from "../../lib/dateFormat";
import { invoiceDocumentNumber, invoiceDocumentLabel, isFiscalInvoice } from "../../lib/invoiceDocument";

type ClubOption = {
  id: string;
  name: string;
  city?: string | null;
  county?: string | null;
  member_count?: number;
  cost_per_athlete_cents?: number;
};

export default function Billing() {
  const { t } = useTranslation();
  const { activeRole } = useAuth();
  const [activeTab, setActiveTab] = useState<"invoices" | "generate">("invoices");
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [retryingOblio, setRetryingOblio] = useState<string | null>(null);
  const [clubs, setClubs] = useState<ClubOption[]>([]);
  const [selectedClubId, setSelectedClubId] = useState<string>("");
  const [generatingSingleClub, setGeneratingSingleClub] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "paid" | "pending">("all");

  // For generating invoices
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // For single club invoice with custom billing period
  const today = new Date().toISOString().split("T")[0];
  const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1).toISOString().split("T")[0];
  const [billingStartDate, setBillingStartDate] = useState(firstDayOfMonth);
  const [billingEndDate, setBillingEndDate] = useState(today);

  // Server-side preview of the same calculation /generate-for-club would
  // run, so the "Estimated Total" we show always matches the payment
  // link Stripe will eventually produce. Previously the UI multiplied
  // athletes * rate * (days/30) ignoring per-athlete join_at and was
  // wildly off whenever new athletes joined mid-period.
  const [preview, setPreview] = useState<{
    cost_per_athlete_cents: number;
    total_days: number;
    daily_rate_cents: number;
    total_athletes_in_club: number;
    full_period_athletes: number;
    pro_rata_athletes: number;
    pro_rata_days_avg: number;
    billable_athletes: number;
    total_amount_cents: number;
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    loadInvoices();
    loadClubs();
  }, []);

  // Refresh the server preview whenever club or period changes.
  // Debounced so each keystroke in DateInput doesn't fire a request.
  useEffect(() => {
    if (!selectedClubId || !billingStartDate || !billingEndDate) {
      setPreview(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const { data } = await api.get(`/invoices/preview-for-club/${selectedClubId}`, {
          params: { period_start: billingStartDate, period_end: billingEndDate },
        });
        setPreview(data);
      } catch (err: any) {
        setPreview(null);
        setPreviewError(err?.response?.data?.error || "Nu s-a putut calcula totalul");
      } finally {
        setPreviewLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [selectedClubId, billingStartDate, billingEndDate]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await getInvoices();
      setInvoices(data);
    } catch (err) {
      console.error("Failed to load invoices:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadClubs = async () => {
    try {
      const { data } = await api.get("/clubs");
      // Backend returns [{ club: {...}, role }], extract clubs
      const clubList = data.map((item: any) => item.club || item);
      setClubs(clubList);
    } catch (err) {
      console.error("Failed to load clubs:", err);
    }
  };

  const handleGenerateInvoices = async () => {
    try {
      setGenerating(true);
      const result = await generateMonthlyInvoices(selectedMonth, selectedYear);
      alert(`${result.message}`);
      loadInvoices();
    } catch (err: any) {
      console.error("Failed to generate invoices:", err);
      alert(err.response?.data?.error || "Generarea facturilor a eșuat");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateSingleClubInvoice = async () => {
    if (!selectedClubId) {
      alert("Selectează mai întâi un club");
      return;
    }

    if (!billingStartDate || !billingEndDate) {
      alert("Selectează datele perioadei de facturare");
      return;
    }

    if (new Date(billingStartDate) > new Date(billingEndDate)) {
      alert("Data de început trebuie să fie înaintea datei de sfârșit");
      return;
    }

    try {
      setGeneratingSingleClub(true);
      const result = await generateInvoiceForClub(
        selectedClubId,
        billingStartDate,
        billingEndDate
      );

      if (result.payment_link) {
        navigator.clipboard.writeText(result.payment_link);
        alert(`Factură generată!\n\nLink de plată:\n${result.payment_link}\n\n(Link-ul a fost copiat în clipboard)`);
      } else {
        alert(result.message);
        if (result.stripe_error) {
          console.warn("Stripe error:", result.stripe_error);
        }
      }

      loadInvoices();
    } catch (err: any) {
      console.error("Failed to generate invoice:", err);
      alert(err.response?.data?.error || "Generarea facturii a eșuat");
    } finally {
      setGeneratingSingleClub(false);
    }
  };

  const handleMarkPaid = async (invoiceId: string) => {
    try {
      await markInvoicePaid(invoiceId);
      alert("Factură marcată ca plătită");
      loadInvoices();
    } catch (err: any) {
      console.error("Failed to mark invoice as paid:", err);
      alert(err.response?.data?.error || "Marcarea facturii ca plătită a eșuat");
    }
  };

  const handleStatusChange = async (invoiceId: string, newStatus: string) => {
    try {
      await updateInvoiceStatus(invoiceId, newStatus);
      loadInvoices();
    } catch (err: any) {
      console.error("Failed to update invoice status:", err);
      alert(err.response?.data?.error || "Actualizarea statusului facturii a eșuat");
    }
  };

  const handleDeleteInvoice = async (invoiceId: string, invoiceNumber: string | null) => {
    if (!window.confirm(`Sigur dorești să ștergi factura ${invoiceNumber || invoiceId}? Această acțiune nu poate fi anulată.`)) {
      return;
    }
    try {
      await deleteInvoice(invoiceId);
      alert("Factură ștearsă cu succes");
      loadInvoices();
    } catch (err: any) {
      console.error("Failed to delete invoice:", err);
      alert(err.response?.data?.error || "Ștergerea facturii a eșuat");
    }
  };

  /**
   * Re-issue a paid invoice whose Oblio call failed.
   *
   * Safe to press twice: the backend sends a stable idempotency key, so Oblio
   * returns the document it already has rather than raising a second one.
   */
  const retryOblio = async (invoiceId: string) => {
    setRetryingOblio(invoiceId);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/oblio-retry`);
      alert(`Factură emisă în Oblio: ${data.invoice_number}`);
      loadInvoices();
    } catch (err: any) {
      alert(err.response?.data?.error || "Reemiterea în Oblio a eșuat");
    }
    setRetryingOblio(null);
  };

  const totalRevenue = invoices
    .filter((inv) => inv.paid_at)
    .reduce((sum, inv) => sum + inv.amount_cents / 100, 0);

  const pendingRevenue = invoices
    .filter((inv) => !inv.paid_at)
    .reduce((sum, inv) => sum + inv.amount_cents / 100, 0);

  const isPlatformAdmin = activeRole === "platform_admin";

  // Filter invoices based on search query and status
  const filteredInvoices = invoices.filter((invoice) => {
    // Status filter
    if (statusFilter === "paid" && !invoice.paid_at) return false;
    if (statusFilter === "pending" && invoice.paid_at) return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesClub = invoice.club?.name.toLowerCase().includes(query);
      const matchesNumber = invoiceDocumentNumber(invoice).toLowerCase().includes(query);
      const matchesStripeId = invoice.stripe_invoice_id.toLowerCase().includes(query);
      return matchesClub || matchesNumber || matchesStripeId;
    }

    return true;
  });

  // Copy to clipboard helper
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    alert(`${label} copiat în clipboard!`);
  };

  const months = [
    "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
    "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        {/* Header */}
        <AdminPageHeader
          eyebrow="Platformă"
          title="Facturare și facturi"
          subtitle="Facturare lunară pe baza sportivilor activi (1 EUR per sportiv)"
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
        />

        {/* Revenue Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-50 rounded-xl">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total încasat</p>
                <p className="text-2xl font-bold text-slate-800">{totalRevenue.toFixed(2)} EUR</p>
                <p className="text-xs text-slate-400 mt-0.5">Facturi plătite</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-50 rounded-xl">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-slate-500">Venituri în așteptare</p>
                <p className="text-2xl font-bold text-amber-600">{pendingRevenue.toFixed(2)} EUR</p>
                <p className="text-xs text-slate-400 mt-0.5">Facturi neplătite</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-xl">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total facturi</p>
                <p className="text-2xl font-bold text-slate-800">{invoices.length}</p>
                <p className="text-xs text-slate-400 mt-0.5">Din totdeauna</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setActiveTab("invoices")}
              className={`px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === "invoices"
                  ? "text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50/50"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Toate facturile
              </div>
            </button>
            {isPlatformAdmin && (
              <button
                onClick={() => setActiveTab("generate")}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === "generate"
                    ? "text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50/50"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Generează facturi
                </div>
              </button>
            )}
          </div>

          <div className="p-6">
            {/* Invoices Tab */}
            {activeTab === "invoices" && (
              <div className="space-y-4">
                {/* Search and Filters */}
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  {/* Search */}
                  <div className="flex-1">
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Caută după numele clubului, numărul facturii..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setStatusFilter("all")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        statusFilter === "all"
                          ? "bg-slate-800 text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      Toate ({invoices.length})
                    </button>
                    <button
                      onClick={() => setStatusFilter("paid")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        statusFilter === "paid"
                          ? "bg-emerald-500 text-white"
                          : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                      }`}
                    >
                      Plătite ({invoices.filter(i => i.paid_at).length})
                    </button>
                    <button
                      onClick={() => setStatusFilter("pending")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        statusFilter === "pending"
                          ? "bg-amber-500 text-white"
                          : "bg-amber-50 text-amber-600 hover:bg-amber-100"
                      }`}
                    >
                      În așteptare ({invoices.filter(i => !i.paid_at).length})
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-500 rounded-full animate-spin"></div>
                      <p className="text-slate-500">Se încarcă facturile...</p>
                    </div>
                  </div>
                ) : filteredInvoices.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="p-4 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <p className="text-slate-500">
                      {searchQuery || statusFilter !== "all" ? "Nicio factură nu corespunde căutării" : "Nicio factură găsită"}
                    </p>
                    {isPlatformAdmin && !searchQuery && statusFilter === "all" && (
                      <button
                        onClick={() => setActiveTab("generate")}
                        className="mt-4 text-emerald-600 hover:text-emerald-700 font-medium text-sm"
                      >
                        + Generează primele facturi
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-2 text-xs font-semibold text-slate-500 uppercase">Factură</th>
                          <th className="text-left py-3 px-2 text-xs font-semibold text-slate-500 uppercase">Club</th>
                          <th className="text-right py-3 px-2 text-xs font-semibold text-slate-500 uppercase">Sumă</th>
                          <th className="text-center py-3 px-2 text-xs font-semibold text-slate-500 uppercase">Sportivi</th>
                          <th className="text-left py-3 px-2 text-xs font-semibold text-slate-500 uppercase">Dată</th>
                          <th className="text-center py-3 px-2 text-xs font-semibold text-slate-500 uppercase">Status</th>
                          <th className="text-right py-3 px-2 text-xs font-semibold text-slate-500 uppercase">Acțiuni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInvoices.map((invoice) => (
                          <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-2">
                              <div className="font-medium text-slate-800 text-sm">
                                {invoiceDocumentNumber(invoice)}
                              </div>
                              {!isFiscalInvoice(invoice) && (
                                <div className="text-[11px] font-semibold text-amber-600">Proformă</div>
                              )}
                              {/* A paid invoice with no fiscal number means Oblio
                                  refused it. Left visible rather than silently
                                  never issued. */}
                              {invoice.oblio_error && !isFiscalInvoice(invoice) && (
                                <div className="text-[11px] text-rose-600 max-w-[220px]" title={invoice.oblio_error}>
                                  Oblio: {invoice.oblio_error}
                                </div>
                              )}
                              {invoice.oblio_link && (
                                <a
                                  href={invoice.oblio_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[11px] text-indigo-600 hover:underline"
                                >
                                  Factura Oblio
                                </a>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              <div className="text-sm text-slate-600">{invoice.club?.name || "-"}</div>
                            </td>
                            <td className="py-3 px-2 text-right">
                              <div className="font-semibold text-slate-800 text-sm">
                                {(invoice.amount_cents / 100).toFixed(2)} {invoice.currency}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center">
                              <div className="text-sm text-slate-600">{invoice.athlete_count || "-"}</div>
                            </td>
                            <td className="py-3 px-2">
                              <div className="text-sm text-slate-600">
                                {formatDate(new Date(invoice.created_at))}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center">
                              {isPlatformAdmin ? (
                                <select
                                  value={invoice.paid_at ? "paid" : (invoice.status || "open")}
                                  onChange={(e) => handleStatusChange(invoice.id, e.target.value)}
                                  className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${
                                    invoice.paid_at
                                      ? "bg-emerald-50 text-emerald-600"
                                      : invoice.status === "cancelled"
                                      ? "bg-red-50 text-red-600"
                                      : "bg-amber-50 text-amber-600"
                                  }`}
                                >
                                  <option value="open">ÎN AȘTEPTARE</option>
                                  <option value="paid">PLĂTITĂ</option>
                                  <option value="cancelled">ANULATĂ</option>
                                </select>
                              ) : (
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    invoice.paid_at
                                      ? "bg-emerald-50 text-emerald-600"
                                      : invoice.status === "cancelled"
                                      ? "bg-red-50 text-red-600"
                                      : "bg-amber-50 text-amber-600"
                                  }`}
                                >
                                  {invoice.paid_at ? "PLĂTITĂ" : invoice.status === "cancelled" ? "ANULATĂ" : "ÎN AȘTEPTARE"}
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center justify-end gap-1">
                                {/* PDF Download */}
                                <a
                                  href={`${process.env.REACT_APP_API_URL || "https://igapp-production.up.railway.app"}/invoices/${invoice.id}/pdf?token=${encodeURIComponent(Cookies.get("igrowth_token") || "")}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Descarcă PDF"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </a>

                                {/* Payment Link */}
                                {invoice.stripe_payment_link && (
                                  <>
                                    <a
                                      href={invoice.stripe_payment_link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-1.5 text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                                      title="Deschide link-ul de plată"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                      </svg>
                                    </a>
                                    <button
                                      onClick={() => copyToClipboard(invoice.stripe_payment_link!, "Link-ul de plată")}
                                      className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                      title="Copiază link-ul de plată"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </button>
                                  </>
                                )}

                                {/* Re-issue in Oblio: only for a paid invoice that
                                    never got a fiscal number. */}
                                {isPlatformAdmin && invoice.status === "paid" && !isFiscalInvoice(invoice) && (
                                  <button
                                    onClick={() => retryOblio(invoice.id)}
                                    disabled={retryingOblio === invoice.id}
                                    className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50"
                                    title="Reemite factura fiscală în Oblio"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                  </button>
                                )}

                                {/* Delete button (platform admin only) */}
                                {isPlatformAdmin && (
                                  <button
                                    onClick={() => handleDeleteInvoice(invoice.id, invoiceDocumentNumber(invoice))}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Șterge factura"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Generate Invoices Tab */}
            {activeTab === "generate" && isPlatformAdmin && (
              <div className="space-y-6">
                <div className="p-6 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">
                      Generează facturi lunare
                    </h3>
                  </div>
                  <p className="text-sm text-slate-500 mb-6">
                    Generează facturi pentru toate cluburile pe baza numărului de sportivi activi.
                    Fiecare factură este calculată la 1 EUR per sportiv.
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Lună
                      </label>
                      <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      >
                        {months.map((month, index) => (
                          <option key={index} value={index + 1}>
                            {month}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        An
                      </label>
                      <input
                        type="number"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                        min={2020}
                        max={2030}
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleGenerateInvoices}
                    disabled={generating}
                    className="w-full px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {generating ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Se generează...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Generează facturi
                      </>
                    )}
                  </button>

                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-blue-700">
                        <strong>Notă:</strong> Aceasta va crea facturi pentru {months[selectedMonth - 1]}{" "}
                        {selectedYear}. Fiecare club va fi taxat pe baza tarifului configurat per sportiv activ.
                        Facturile duplicate pentru aceeași lună vor fi omise.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Single Club Invoice Generation */}
                <div className="p-6 bg-violet-50 rounded-xl border border-violet-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-violet-100 rounded-lg">
                      <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">
                      Generează factură pentru un singur club
                    </h3>
                  </div>
                  <p className="text-sm text-slate-500 mb-6">
                    Generează o factură cu link de plată Stripe pentru un club specific.
                    Link-ul de plată poate fi trimis administratorului clubului.
                  </p>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Selectează clubul
                    </label>
                    <select
                      value={selectedClubId}
                      onChange={(e) => setSelectedClubId(e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                    >
                      <option value="">-- Selectează un club --</option>
                      {clubs.map((club) => {
                        const location = [club.city, club.county].filter(Boolean).join(", ");
                        return (
                          <option key={club.id} value={club.id}>
                            {club.name}{location ? ` (${location})` : ""} - {club.member_count || 0} sportivi
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Billing Period Date Pickers */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Data de început facturare
                      </label>
                      <DateInput
                        value={billingStartDate}
                        onChange={(iso) => setBillingStartDate(iso)}
                        max={billingEndDate || today}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Data de sfârșit facturare
                      </label>
                      <DateInput
                        value={billingEndDate}
                        onChange={(iso) => setBillingEndDate(iso)}
                        min={billingStartDate}
                        max={today}
                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
                      />
                    </div>
                  </div>

                  {selectedClubId && (() => {
                    // We deliberately drop the old client-side estimate
                    // (rate * member_count * days/30) and read the figures
                    // straight from the /preview-for-club endpoint so the
                    // displayed total always matches the eventual Stripe
                    // charge — including the per-athlete join-date prorating
                    // backend already does at invoice time.
                    if (previewLoading && !preview) {
                      return (
                        <div className="mb-6 p-4 bg-white rounded-xl border border-violet-200 text-sm text-slate-500 flex items-center gap-3">
                          <div className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
                          Se calculează totalul real...
                        </div>
                      );
                    }
                    if (previewError) {
                      return (
                        <div className="mb-6 p-4 bg-red-50 rounded-xl border border-red-200 text-sm text-red-700">
                          {previewError}
                        </div>
                      );
                    }
                    if (!preview) return null;
                    const rate = preview.cost_per_athlete_cents / 100;
                    const estimatedTotal = preview.total_amount_cents / 100;
                    return (
                      <div className="mb-6 p-4 bg-white rounded-xl border border-violet-200">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Sportivi facturabili</p>
                            <p className="text-lg font-semibold text-slate-800">{preview.billable_athletes}</p>
                            {preview.total_athletes_in_club !== preview.billable_athletes && (
                              <p className="text-xs text-slate-400 mt-0.5">din {preview.total_athletes_in_club} în club</p>
                            )}
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Tarif</p>
                            <p className="text-lg font-semibold text-slate-800">€{rate.toFixed(2)}/sportiv/lună</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Zile de facturare</p>
                            <p className="text-lg font-semibold text-slate-800">{preview.total_days} zile</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Total estimat</p>
                            <p className="text-lg font-semibold text-violet-600">€{estimatedTotal.toFixed(2)}</p>
                          </div>
                        </div>
                        {(preview.full_period_athletes > 0 || preview.pro_rata_athletes > 0) && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                            <p>
                              <span className="font-medium text-slate-700">{preview.full_period_athletes}</span> sportivi pentru perioada întreagă ({preview.total_days} zile)
                            </p>
                            {preview.pro_rata_athletes > 0 && (
                              <p>
                                <span className="font-medium text-slate-700">{preview.pro_rata_athletes}</span> sportivi pro-rata · medie {preview.pro_rata_days_avg} zile/sportiv
                              </p>
                            )}
                          </div>
                        )}
                        <p className="text-xs text-slate-500 mt-3">
                          * Totalul reflectă calculul backend-ului — sportivii care s-au înscris în timpul perioadei sunt facturați pro-rata de la data alăturării.
                        </p>
                      </div>
                    );
                  })()}

                  <button
                    onClick={handleGenerateSingleClubInvoice}
                    disabled={generatingSingleClub || !selectedClubId}
                    className="w-full px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {generatingSingleClub ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Se generează...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        Generează factură cu link de plată
                      </>
                    )}
                  </button>

                  <div className="mt-4 p-3 bg-violet-100 border border-violet-200 rounded-xl">
                    <p className="text-xs text-violet-700">
                      Link-ul de plată va fi copiat automat în clipboard la generare.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
